'use client'

import type { GetCategoryArticlesResult } from '@/helpers/data'
import { getCategoryArticles } from '@/helpers/data'
import { transformNewsArticlesToCards, transformNewsArticleToFeature } from '@/helpers/transforms'
import type { FC } from 'react'
import { useMemo, useState, useTransition } from 'react'
import { FontAwesomeIcon } from '../atoms/FontAwesomeIcon'
import { CardListBlock } from '../blocks/CardListBlock'
import FeatureCardBlock from '../blocks/FeatureCardBlock'

export type LoadNewsBlockProps = {
	categoryId: string
	data: GetCategoryArticlesResult
}

const loadMoreClasses = 'flex w-fit cursor-pointer items-center gap-x-sm text-button text-blue'

export const LoadNewsBlock: FC<LoadNewsBlockProps> = ({ categoryId, data }) => {
	const [{ articles, canLoadMore }, setData] = useState(data)

	const { feature, cards } = useMemo(() => {
		const [first, ...rest] = articles
		return {
			feature: transformNewsArticleToFeature(first),
			cards: transformNewsArticlesToCards(rest),
		}
	}, [articles])

	const [isPending, startTransition] = useTransition()
	const onLoadMore = () => {
		startTransition(async () => {
			const limit = articles.length + 16
			const data = await getCategoryArticles(categoryId, limit)
			setData(data)
		})
	}

	return (
		<div className="space-y-2xl">
			<div className="space-y-5xl">
				{feature && <FeatureCardBlock card={feature} />}
				{cards.length > 0 && <CardListBlock cards={cards} />}
			</div>
			{canLoadMore && (
				<div className="flex justify-center">
					{isPending ? (
						<div className={loadMoreClasses}>
							<span>Loading...</span>
							<FontAwesomeIcon icon={['fas', 'spinner-third']} size="md" className="animate-spin" />
						</div>
					) : (
						<button className={loadMoreClasses} onClick={onLoadMore}>
							<span>Load More</span>
							<FontAwesomeIcon icon={['fas', 'plus']} size="md" />
						</button>
					)}
				</div>
			)}
		</div>
	)
}
